import {
  type CSSProperties,
  type FC,
  type MouseEvent,
  useMemo,
  useRef,
  useState
} from 'react'
import { Chart } from '@/components/Radar/Chart'
import { Label } from '@/components/Radar/Label'
import { Legend } from '@/components/Radar/Legend'
import { cn } from '@/lib/utils'
import type { Item, Quadrant, Ring } from '../../../../types'
import styles from './Radar.module.css'

export interface RadarProps {
  size?: number
  quadrants: readonly Quadrant[]
  rings: readonly Ring[]
  items: readonly Item[]
}

export const Radar: FC<RadarProps> = ({
  size = 800,
  quadrants = [],
  rings = [],
  items = []
}) => {
  const radarRef = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState({
    show: false,
    text: '',
    color: '',
    x: 0,
    y: 0
  })

  const tooltipStyle = useMemo(
    () =>
      ({
        left: tooltip.x,
        top: tooltip.y,
        ...(tooltip.color ? { '--tooltip': tooltip.color } : undefined)
      }) as CSSProperties,
    [tooltip]
  )

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const link =
      e.target instanceof Element && e.target.closest('a[data-tooltip]')
    if (link) {
      const text = link.getAttribute('data-tooltip') || ''
      const color = link.getAttribute('data-tooltip-color') || ''
      const linkRect = link.getBoundingClientRect()
      const radarRect = radarRef.current?.getBoundingClientRect() || {
        left: 0,
        top: 0
      }

      // Adjusting tooltip position to be relative to the radar container
      const x = linkRect.left - radarRect.left + linkRect.width / 2
      const y = linkRect.top - radarRect.top

      setTooltip({
        text,
        color,
        show: !!text,
        x,
        y
      })
    } else {
      if (tooltip.show) {
        setTooltip({ ...tooltip, show: false })
      }
    }
  }

  const handleMouseLeave = () => {
    setTooltip({ ...tooltip, show: false })
  }

  return (
    <div
      ref={radarRef}
      data-testid="radar-container"
      className={styles.radar}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <Chart
        className={styles.chart}
        size={size}
        quadrants={quadrants.map(q => ({
          ...q,
          'data-testid': `radar-quadrant-${q.id}`
        }))}
        rings={rings.map(r => ({
          ...r,
          'data-testid': `radar-ring-${r.id}`
        }))}
        items={items.map(i => ({
          ...i,
          'data-testid': `radar-blip-${i.id}`
        }))}
      />
      <div className={styles.labels}>
        {quadrants.map(quadrant => (
          <Label key={quadrant.id} quadrant={quadrant} />
        ))}
      </div>
      <Legend />
      <span
        className={cn(styles.tooltip, tooltip.show && styles.isShown)}
        style={tooltipStyle}
      >
        {tooltip.text}
      </span>
    </div>
  )
}

export default Radar
